import React, { useEffect, useState } from "react";
import ButtonComponent from "../../components/ButtonComponent";
import AccordionComponent from "../../components/AccordionComponent";
import { useAppDispatch, useAppSelector } from "../../redux/app/hooks";
import { useNavigate } from "react-router";
import { getAllCart, removeCartData } from "../../redux/features/leadUserCart/leadUserCart";
import { nanoid } from "nanoid";
import { useParams } from "react-router-dom";
import ModalComponent from "../../components/ModalComponent";
import backArrow from "../../assets/icons/up arrow.svg";
import { toastComponent } from "../../components/ToastComponent";
import { Badge, Separator } from "../../components/ui";
import { ShoppingCart } from "lucide-react";
import AppLoader from "../../components/AppLoader";
import { useLoading } from "../../hooks/useLoading";

type Props = {};

const UserCatalogueOrder = (props: Props) => {
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const cartDetails = useAppSelector((state) => state.leadUserCart.row);
  const cartOverView = useAppSelector((state) => state.leadUserCart.summary);
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const cartRow = useAppSelector((state) => state.leadUserCart.row);

  // Loading states
  const { isLoading: initialLoading, withLoading, stopLoading } = useLoading(false);

  useEffect(() => {
    const loadCartData = async () => {
      window.scrollTo(0, 0);
      await withLoading(() => dispatch(getAllCart()));
    };
    loadCartData();
  }, []);

  // Check if cart data already exists to stop loading
  useEffect(() => {
    if (cartRow && cartRow.length > 0) {
      stopLoading();
    }
  }, [cartRow, stopLoading]);

  async function handleRemoveall() {
    setIsLoading(true);
    try {
      const idArray: any = [];
      cartDetails.forEach((rowItem) => {
        rowItem.list.forEach((listItem) => {
          idArray.push(listItem.id);
        });
      });

      const res = await dispatch(removeCartData({ cart_id: idArray }));
      toastComponent(res.payload.message, "success");
      await dispatch(getAllCart());
    } catch (error) {
      toastComponent("Failed to remove items from cart", "error");
    } finally {
      setIsLoading(false);
      setDeleteModal(false);
    }
  }

  return (
    <>
      {initialLoading && <AppLoader message="Loading cart..." />}

      {deleteModal ? (
        <ModalComponent setShow={setDeleteModal} show={deleteModal}>
          <div className="flex flex-col h-full">
            <div className="text-primary font-bold px-6 py-4 border-b flex flex-row justify-between">
              Remove From Cart?
              <button className="text-lg font-bold cursor-pointer hover:text-gray-600 transition-colors" onClick={() => setDeleteModal(false)}>
                ✕
              </button>
            </div>
            <div className="min-h-[3rem] px-6 py-4">
              <p className="text-gray-600">Are you sure you want to remove all items from your cart?</p>
              <p className="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
            </div>
            <div className="flex flex-row justify-between px-6 gap-4 py-4">
              <ButtonComponent variant="primary" CTA="Cancel" onClick={() => setDeleteModal(false)} style={{ flex: 1 }} />
              <ButtonComponent
                variant="danger"
                CTA={isLoading ? "Removing..." : "Remove All"}
                onClick={handleRemoveall}
                disabled={isLoading}
                style={{ flex: 1, backgroundColor: "rgb(220, 38, 38)" }}
              />
            </div>
          </div>
        </ModalComponent>
      ) : null}

      <div className="min-h-screen bg-white">
        {/* Top Bar */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button className="flex flex-row items-center text-primary hover:text-primary-dark transition-colors font-medium gap-2" onClick={() => navigate(`/catalogues/${id}`)}>
            <img src={backArrow} alt="" className="h-4 w-4" />
            Back to Catalogue
          </button>
        </div>

        {cartDetails.length ? (
          <>
            {/* Header */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-end justify-between">
                <div>
                  <h1 className="text-2xl md:text-3xl font-semibold text-primary">Shopping Cart</h1>
                  <p className="text-muted-foreground">Review your items before placing your order</p>
                </div>
                <Badge variant="secondary" className="hidden md:inline-flex gap-1">
                  <ShoppingCart className="w-4 h-4" /> {cartOverView.total_qty} pcs
                </Badge>
              </div>
            </div>

            <Separator className="mt-4" />

            {/* Cart Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
              {/* Cart Items */}
              {cartDetails.map((i) => (
                <AccordionComponent key={nanoid()} list={i.list} name={i.name} totalQunty={i.total_qty} totalwight={i.total_net_weight} />
              ))}

              {/* Summary Bar */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
                <div className="lg:col-span-8 bg-white rounded-lg border shadow-sm">
                  <div className="flex flex-col md:flex-row items-center md:items-stretch justify-between gap-3 p-4">
                    <ButtonComponent
                      variant="danger"
                      CTA="Remove All"
                      onClick={() => setDeleteModal(true)}
                      style={{ width: "100%", maxWidth: "150px", padding: "8px 16px", fontSize: "14px", backgroundColor: "rgb(220, 38, 38)" }}
                    />
                    <div className="flex-1 grid grid-cols-3 gap-3 w-full">
                      <div className="rounded-xl bg-gray-50 p-3 text-center">
                        <div className="text-xs text-gray-600">Pieces</div>
                        <div className="font-semibold text-primary">{cartOverView.total_qty}</div>
                      </div>
                      <div className="rounded-xl bg-gray-50 p-3 text-center">
                        <div className="text-xs text-gray-600">Gross Weight</div>
                        <div className="font-semibold text-primary">{cartOverView.total_gross_weight} gms</div>
                      </div>
                      <div className="rounded-xl bg-gray-50 p-3 text-center">
                        <div className="text-xs text-gray-600">Net Weight</div>
                        <div className="font-semibold text-primary">{cartOverView.total_net_weight} gms</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-4 bg-white rounded-lg border shadow-sm">
                  <div className="p-4 flex flex-col gap-3">
                    <div className="text-sm text-gray-600">Ready to checkout?</div>
                    <ButtonComponent
                      CTA="Place Order"
                      variant="dynamic"
                      onClick={() => navigate(`/catalogues/${id}/order-cart/user-preference`)}
                      buttonType="submit"
                      style={{ width: "100%" }}
                    />
                    <p className="text-xs text-gray-500">By placing the order, you agree to our terms and conditions. Metal rate will be fixed on confirmation.</p>
                  </div>
                </div>
              </div>
            </div>

          </>
        ) : (
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center max-w-md mx-auto px-4">
              <div className="mb-8">
                <img src="/empty cart image.png" alt="Empty cart" className="mx-auto w-64 h-64 object-contain" />
              </div>
              <h1 className="text-3xl font-bold text-primary mb-4">Your Cart is Empty!</h1>
              <p className="text-xl text-gray-600 mb-8">Add some beautiful jewelry to your cart to get started.</p>
              <ButtonComponent CTA="Shop Now" onClick={() => navigate(`/catalogues/${id}`)} style={{ width: "200px" }} variant="primary" />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UserCatalogueOrder;
